import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Search, Edit, Trash2, Package, AlertTriangle, Upload } from 'lucide-react';
import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';
import BulkUploadModal from './BulkUploadModal';

const Products = () => {
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);

  const [formData, setFormData] = useState({
    categoryId: '',
    brand: '',
    model: '',
    name: '',
    partNumber: '',
    hsnCode: '',
    gstRate: '18',
    gstInclusive: false,
    purchaseRate: '',
    saleRate: '',
    minStockLimit: '5',
    openingStock: '0',
    unit: 'PCS',
    description: ''
  });

  // Fetch categories
  const { data: categoriesData } = useQuery('categories', async () => {
    const response = await api.get('/api/categories');
    return response.data.data;
  });

  // Fetch products
  const { data: productsData, isLoading } = useQuery(
    ['products', selectedBranch?.id, search, categoryFilter],
    async () => {
      const params = {
        branchId: selectedBranch?.id,
        search,
        categoryId: categoryFilter
      };
      const response = await api.get('/api/products', { params });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch,
    }
  );

  const createProductMutation = useMutation(
    async (productData) => {
      const response = await api.post('/api/products', productData);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Product created successfully!');
        queryClient.invalidateQueries('products');
        resetForm();
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create product');
      }
    }
  );

  const updateProductMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/products/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Product updated successfully!');
        queryClient.invalidateQueries('products');
        resetForm();
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update product');
      }
    }
  );

  const deleteProductMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/api/products/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Product deleted successfully!');
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete product');
      }
    }
  );

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteProductMutation.mutate(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.purchaseRate || parseFloat(formData.purchaseRate) < 0) {
      toast.error('Purchase Rate is mandatory');
      return;
    }

    const productData = {
      ...formData,
      branchId: selectedBranch?.id,
      gstRate: parseFloat(formData.gstRate) || 0,
      purchaseRate: parseFloat(formData.purchaseRate) || 0,
      saleRate: parseFloat(formData.saleRate) || 0,
      minStockLimit: parseInt(formData.minStockLimit) || 0,
      openingStock: parseInt(formData.openingStock) || 0
    };

    if (editingProduct) {
      delete productData.openingStock;
      updateProductMutation.mutate({ id: editingProduct.id, data: productData });
    } else {
      createProductMutation.mutate(productData);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      categoryId: product.categoryId,
      brand: product.brand,
      model: product.model,
      name: product.name,
      partNumber: product.partNumber || '',
      hsnCode: product.hsnCode || '',
      gstRate: product.gstRate.toString(),
      gstInclusive: product.gstInclusive,
      purchaseRate: product.purchaseRate.toString(),
      saleRate: product.saleRate.toString(),
      minStockLimit: product.minStockLimit.toString(),
      openingStock: (product.openingStock ?? 0).toString(),
      unit: product.unit,
      description: product.description || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      categoryId: '',
      brand: '',
      model: '',
      name: '',
      partNumber: '',
      hsnCode: '',
      gstRate: '18',
      gstInclusive: false,
      purchaseRate: '',
      saleRate: '',
      minStockLimit: '5',
      openingStock: '0',
      unit: 'PCS',
      description: ''
    });
    setEditingProduct(null);
  };

  const products = productsData?.products || [];
  const categories = categoriesData || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Master</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage product catalog and inventory
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => setShowBulkModal(true)}
            className="btn-secondary inline-flex items-center"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Upload
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary inline-flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div>
            <select
              className="input-field"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card p-0">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Brand</th>
                <th>Model</th>
                <th>Item Name</th>
                <th>Part No</th>
                <th>HSN</th>
                <th>GST %</th>
                <th>Sale Rate</th>
                <th>Stock</th>
                <th>Min Limit</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td>{product.category?.name}</td>
                  <td>{product.brand}</td>
                  <td>{product.model}</td>
                  <td>
                    <div className="font-medium text-gray-900">{product.name}</div>
                  </td>
                  <td>{product.partNumber || '-'}</td>
                  <td>{product.hsnCode || '-'}</td>
                  <td>
                    {product.gstRate}%
                    {product.gstInclusive && (
                      <span className="text-xs text-gray-500"> (Inc)</span>
                    )}
                  </td>
                  <td className="font-medium">{currencySymbol}{product.saleRate.toLocaleString()}</td>
                  <td>
                    <span className={`${
                      product.currentStock <= product.minStockLimit
                        ? 'text-red-600 font-semibold'
                        : 'text-gray-900'
                    }`}>
                      {product.currentStock} {product.unit}
                    </span>
                  </td>
                  <td>{product.minStockLimit} {product.unit}</td>
                  <td>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded-md hover:bg-blue-50"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="text-red-600 hover:text-red-800 transition-colors p-1 rounded-md hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {products.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No products found</p>
          </div>
        )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Category *</label>
                  <select
                    className="input-field"
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Brand *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Model *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Item Name *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Part Number</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.partNumber}
                    onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">HSN Code</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.hsnCode}
                    onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">GST % *</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      className="input-field flex-1"
                      value={formData.gstRate}
                      onChange={(e) => setFormData({ ...formData, gstRate: e.target.value })}
                      step="0.01"
                      required
                    />
                    <label className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formData.gstInclusive}
                        onChange={(e) => setFormData({ ...formData, gstInclusive: e.target.checked })}
                        className="rounded"
                      />
                      <span>Inclusive</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="form-label">Sale Rate</label>
                  <input
                    type="number"
                    className="input-field"
                    value={formData.saleRate}
                    onChange={(e) => setFormData({ ...formData, saleRate: e.target.value })}
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="form-label">Purchase Rate *</label>
                  <input
                    type="number"
                    className="input-field"
                    value={formData.purchaseRate}
                    onChange={(e) => setFormData({ ...formData, purchaseRate: e.target.value })}
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Set Limit (Min Stock) *</label>
                  <input
                    type="number"
                    className="input-field"
                    value={formData.minStockLimit}
                    onChange={(e) => setFormData({ ...formData, minStockLimit: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Opening Stock</label>
                  <input
                    type="number"
                    className="input-field"
                    value={formData.openingStock}
                    onChange={(e) => setFormData({ ...formData, openingStock: e.target.value })}
                    disabled={!!editingProduct}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="form-label">Unit</label>
                  <select
                    className="input-field"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  >
                    <option value="PCS">PCS</option>
                    <option value="KG">KG</option>
                    <option value="LITER">LITER</option>
                    <option value="METER">METER</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  className="input-field"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createProductMutation.isLoading || updateProductMutation.isLoading}
                  className="btn-primary"
                >
                  {(createProductMutation.isLoading || updateProductMutation.isLoading) ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    editingProduct ? 'Update Product' : 'Create Product'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={() => queryClient.invalidateQueries('products')}
      />
    </div>
  );
};

export default Products;

